/**
 * ReadinessSection — the "Application Readiness" block on `/grants/:id`.
 *
 * Renders between the Eligibility Snapshot and the AI Match section.
 * Fetches `/api/grants/{grant_id}/document-readiness` once per grant.
 *
 * Layout budget: internal padding + one 44px header row + one 28px bar
 * + N checklist rows (each ~48px including borders). To keep the
 * Eligibility Snapshot above-the-fold at 1366×768 (spec constraint),
 * this section MUST stay under ~360px tall for typical 4-6 required
 * doc grants. Achieved via:
 *   - compact heading (kicker + big number in a single row)
 *   - progress bar as a thin 4px line, not a chunky visual
 *   - checklist rows use text-sm not text-base
 *   - "Upload {type}" link is inline on the same row (no wrapping)
 *
 * States:
 *   - loading   → subtle skeleton
 *   - error     → dismissible inline banner (formatApiError)
 *   - null_pct  → "This grant does not list specific document requirements"
 *                 (grant.required_documents == [])
 *   - result    → percentage + threshold label + progress bar + checklist
 *
 * Voice discipline: threshold labels are exactly
 *   100        → "Fully documented"       (green)
 *   80-99      → "Nearly ready"           (lime)
 *   50-79      → "Documents needed"       (amber)
 *   0-49       → "Documents needed"       (red)
 * — no "You have X%" or "You've completed X%" framing (per CP2 spec).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ClipboardCheck,
  UploadCloud,
  Loader2,
  X,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";

// Reverse map from doc_type → the tab category on `/documents` so the
// "Upload {type}" link can prefill the correct tab. Keeping this
// alongside the section (not importing from backend) so the frontend
// stays self-contained.
const DOC_CATEGORY_HINT = {
  ein_letter: "business_foundation",
  articles_of_organization: "business_foundation",
  articles_of_incorporation: "business_foundation",
  operating_agreement: "business_foundation",
  business_license: "business_foundation",
  certificate: "business_foundation",
  tax_return: "financial",
  profit_and_loss: "financial",
  balance_sheet: "financial",
  bank_statement: "financial",
  business_plan: "grant",
  project_budget: "grant",
  resume: "grant",
  capability_statement: "grant",
  letter_of_support: "grant",
  certification: "grant",
  uei_sam_documentation: "grant",
  budget_narrative: "grant",
  project_pitch: "grant",
  feasibility_study: "grant",
};

function thresholdMeta(pct) {
  if (pct === null || pct === undefined) return null;
  if (pct >= 100) return { label: "Fully documented", color: "#10B981", track: "rgba(16,185,129,0.20)" };
  if (pct >= 80) return { label: "Nearly ready", color: "#84CC16", track: "rgba(132,204,22,0.20)" };
  if (pct >= 50) return { label: "Documents needed", color: "#FBBF24", track: "rgba(251,191,36,0.20)" };
  return { label: "Documents needed", color: "#EF4444", track: "rgba(239,68,68,0.22)" };
}

function StateIcon({ state }) {
  if (state === "present") {
    return <CheckCircle2 data-testid="readiness-icon-present" className="w-4 h-4 text-emerald-400 flex-shrink-0" />;
  }
  if (state === "warning") {
    return <AlertTriangle data-testid="readiness-icon-warning" className="w-4 h-4 text-amber-400 flex-shrink-0" />;
  }
  return <XCircle data-testid="readiness-icon-missing" className="w-4 h-4 text-red-400 flex-shrink-0" />;
}

function ChecklistRow({ item }) {
  const uploadHref = useMemo(() => {
    const cat = DOC_CATEGORY_HINT[item.doc_type];
    if (!cat) return "/documents";
    const params = new URLSearchParams({ category: cat, doc_type: item.doc_type });
    return `/documents?${params.toString()}`;
  }, [item.doc_type]);

  return (
    <li
      data-testid={`readiness-row-${item.doc_type}`}
      className="py-2.5 flex items-start gap-3"
      style={{ borderTop: "1px dashed var(--v2-border)" }}
    >
      <StateIcon state={item.state} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-v2-text font-medium">
          {item.display_name}
          {item.state === "missing" && (
            <span
              data-testid={`readiness-row-hint-${item.doc_type}`}
              className="ml-2 text-[11px] text-v2-textMuted font-normal"
            >
              Not yet uploaded
            </span>
          )}
          {item.state === "present" && (
            <span className="ml-2 text-[11px] text-emerald-400/80 font-normal">
              Ready
            </span>
          )}
        </p>
        {item.state === "warning" && item.note && (
          <p
            data-testid={`readiness-row-note-${item.doc_type}`}
            className="mt-0.5 text-[11px] text-amber-300/80 leading-relaxed"
          >
            {item.note}
          </p>
        )}
      </div>
      {item.state === "missing" && (
        <Link
          to={uploadHref}
          data-testid={`readiness-row-upload-${item.doc_type}`}
          className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-v2-accent hover:text-white transition-colors"
        >
          <UploadCloud className="w-3 h-3" />
          Upload {item.display_name}
        </Link>
      )}
    </li>
  );
}

export default function ReadinessSection({ grantId }) {
  const [state, setState] = useState({
    loading: true,
    summary: null,
    error: null,
  });
  const [dismissedError, setDismissedError] = useState(false);

  useEffect(() => {
    if (!grantId) return;
    let cancelled = false;
    setState({ loading: true, summary: null, error: null });
    api
      .get(`/grants/${grantId}/document-readiness`)
      .then((res) => {
        if (!cancelled) setState({ loading: false, summary: res.data, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, summary: null, error: formatApiError(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [grantId]);

  if (state.loading) {
    return (
      <section
        data-testid="grant-readiness-section-loading"
        className="mt-4 rounded-3xl p-4 flex items-center gap-2 text-xs text-v2-textMuted"
        style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading Application Readiness…
      </section>
    );
  }

  if (state.error && !dismissedError) {
    return (
      <section
        data-testid="grant-readiness-section-error"
        role="alert"
        className="mt-4 rounded-3xl p-4 flex items-start gap-3"
        style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.35)" }}
      >
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" />
        <p className="flex-1 text-sm text-v2-text">{state.error}</p>
        <button
          type="button"
          onClick={() => setDismissedError(true)}
          data-testid="grant-readiness-section-error-dismiss"
          className="flex-shrink-0 text-v2-textMuted hover:text-v2-text"
          aria-label="Dismiss error"
        >
          <X className="w-4 h-4" />
        </button>
      </section>
    );
  }

  const summary = state.summary;
  if (!summary) return null;

  // Zero-required grant → sponsor didn't list specific docs.
  if (summary.readiness_percentage === null || summary.total_required === 0) {
    return (
      <section
        data-testid="grant-readiness-section-null"
        className="mt-4 rounded-3xl p-5"
        style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}
      >
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-v2-accent" />
          <p className="text-xs uppercase tracking-[0.28em] text-v2-textMuted">
            Application Readiness
          </p>
        </div>
        <p className="mt-3 text-sm text-v2-text">
          This grant does not list specific document requirements — check
          the sponsor's official page for details.
        </p>
      </section>
    );
  }

  const meta = thresholdMeta(summary.readiness_percentage);

  return (
    <section
      data-testid="grant-readiness-section"
      className="mt-4 rounded-3xl p-5"
      style={{ background: "var(--v2-surface)", border: "1px solid var(--v2-border)" }}
    >
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-v2-accent" />
          <p className="text-xs uppercase tracking-[0.28em] text-v2-textMuted">
            Application Readiness
          </p>
        </div>
        <div className="flex items-baseline gap-3">
          <span
            data-testid="grant-readiness-percentage"
            className="text-[30px] font-semibold tracking-tight"
            style={{ color: meta.color }}
          >
            {summary.readiness_percentage}%
          </span>
          <span
            data-testid="grant-readiness-threshold-label"
            className="text-xs font-semibold uppercase tracking-[0.18em]"
            style={{ color: meta.color }}
          >
            {meta.label}
          </span>
        </div>
      </header>

      <div
        data-testid="grant-readiness-progress-track"
        className="mt-3 h-1.5 rounded-full overflow-hidden"
        style={{ background: meta.track }}
      >
        <div
          data-testid="grant-readiness-progress-fill"
          className="h-full transition-all"
          style={{
            width: `${Math.max(4, summary.readiness_percentage)}%`,
            background: meta.color,
          }}
        />
      </div>

      <p className="mt-3 text-[11px] text-v2-textMuted">
        {summary.present_count} on file · {summary.warning_count} to review · {summary.missing_count} to upload
      </p>

      <ul
        data-testid="grant-readiness-checklist"
        className="mt-3"
      >
        {summary.required_documents.map((item) => (
          <ChecklistRow key={item.doc_type} item={item} />
        ))}
      </ul>
    </section>
  );
}
